import { ref, computed } from 'vue'
import { timesConvert } from '@/utils/helper'
import { get, min, max, filter } from 'lodash'
import dayjs from '@/lib/dayjs'
import { i18n } from '@/plugins/i18n/i18n'

/**
 * 可用時間管理的 Vue composable
 * 提供時間格線生成、時段篩選、狀態判定與點擊處理等功能
 */
export const useAvailableTimeSpace = () => {
  /** @type {import('vue').Ref<string[]>} 所有可能的時間欄標題 (HH:mm 格式) */
  const times = ref([])

  /** @type {import('vue').Ref<string[]>} 實際有資料的時間集合，用於縮小顯示範圍 */
  const show = ref([])

  /**
   * 將時間字串轉換為分鐘數
   * @param {string} time - 時間字串，格式為 "HH:mm"
   * @returns {number} 從 00:00 開始的分鐘數
   */
  const timeToNum = (time) => {
    const [h, m] = time.split(':')
    return (+h) * 60 + (+m)
  }

  /**
   * 根據實際資料範圍過濾的時間欄
   * @type {import('vue').ComputedRef<string[]>}
   */
  const filteredTimes = computed(() => {
    if (!show.value.length) return times.value
    const nums = show.value.map(timeToNum)
    const minT = min(nums)
    const maxT = max(nums)
    return filter(times.value, (t) => {
      const n = timeToNum(t)
      return n >= minT && n <= maxT
    })
  })

  /**
   * 根據預約參數設定生成時間欄
   * @param {Object} config - 預約時間設定
   * @param {number} config.timeUnit - 時間單位（分鐘）
   * @param {number} config.timeUnitOffset - 時間偏移量（分鐘）
   */
  const genTimes = (config) => {
    times.value = []
    const end = 1440 // 24 小時 = 1440 分鐘
    const unit = get(config, 'timeUnit')
    const offset = get(config, 'timeUnitOffset')
    let time = 0
    while (time + offset < end) {
      times.value.push(timesConvert(time + offset))
      time += unit
    }
  }

  /**
   * 將 API 回傳的時間資料附加到項目列表中
   * @param {Object} params - 附加參數
   * @param {Array} params.items - 要附加資料的項目陣列
   * @param {Array} params.results - API 回傳的結果陣列
   * @param {Function} params.getResultForItem - 取得特定項目對應結果的函式
   * @param {import('vue').Ref<string[]>} [params.showRef] - 要更新的 show ref，預設使用內建的 show
   */
  const attachAvailableTimes = ({
    items,
    results,
    getResultForItem,
    showRef = show,
  }) => {
    showRef.value = []
    items.forEach((item, idx) => {
      // 確保每個項目都有 availableTimes 屬性
      if (!item.availableTimes) item.availableTimes = []
      if (!item.availableTimes2) item.availableTimes2 = { times: [] }

      // 清空並設置 availableTimes
      item.availableTimes.splice(0, item.availableTimes.length)
      const res = getResultForItem(item, idx, results)
      if (!res) return

      // 使用陣列方法更新，保持響應性
      item.availableTimes.push(...(Array.isArray(res) ? res : [res]))

      // 建立包含 textTime 的 availableTimes2
      const timesWithText = (get(res, 'times', []) || []).map((timeItem) => ({
        ...timeItem,
        textTime: dayjs(timeItem.start).format('HH:mm'),
      }))

      // 更新 availableTimes2，保持響應性
      Object.assign(item.availableTimes2, { ...res, times: timesWithText })

      // 收集所有時間到 show 中
      showRef.value.push(...timesWithText.map(t => t.textTime))
    })

    // 去重
    showRef.value = Array.from(new Set(showRef.value))
  }

  /**
   * 根據表格 scope 取得時段狀態資訊
   * @param {Object} scope - Element UI 表格的 slot-scope 物件
   * @param {Object} scope.row - 表格行資料
   * @param {Object} scope.column - 表格欄資料
   * @returns {Object} 時段狀態物件
   * @returns {string} returns.status - 狀態：'empty'|'available'|'booking'|'ban'
   * @returns {number} [returns.availableVolume] - 可預約數量
   */
  const periodBlock = (scope) => {
    const times2 = get(scope, 'row.availableTimes2.times', [])
    const label = get(scope, 'column.label')

    if (!times2?.length) return { status: 'empty' }

    const found = times2.find((timeItem) => {
      // 處理 24:00 的特殊情況
      if (timeItem.textTime === '00:00' && label === '24:00') return true
      return timeItem.textTime === label
    })

    return found
      ? { status: found.status, availableVolume: found.availableVolume }
      : { status: 'empty' }
  }

  /**
   * 將狀態轉換為顯示文字
   * @param {Object} statusInfo - 狀態資訊
   * @param {string} statusInfo.status - 狀態
   * @param {number} [statusInfo.availableVolume] - 可預約數量
   * @returns {string} 顯示文字
   */
  const trans = ({ status, availableVolume }) => {
    const dict = {
      empty: i18n.t('shiftCalendar.status.empty.text'),
      available: i18n.t('shiftCalendar.status.available.text'),
      booking: i18n.t('shiftCalendar.status.booking.text'),
      ban: i18n.t('shiftCalendar.status.ban.text'),
    }

    return availableVolume > 0 ? `${dict[status]}(${availableVolume})` : dict[status]
  }

  /**
 * 建立時間格點擊處理器的工廠函式
 *
 * @param {Object} options - 配置選項
 * @param {Function} options.periodBlockFn - 取得時段狀態的函式
 * @param {import('vue').Ref<Date>} options.searchDateRef - 搜尋日期的 ref
 * @param {Object} options.dialog - 對話框狀態物件，需包含 create 屬性
 * @param {Object} options.select - 選擇狀態物件
 * @param {import('vue').Ref} options.createReservationBlockRef - 建立預約區塊的 ref
 * @param {Object} options.loadingControl - 載入狀態控制物件，需包含 createReservationArea 屬性
 * @param {Object} options.messages - 提示訊息
 * @param {string} options.messages.notAvailable - 時段不可用時的提示訊息
 * @param {string} options.messages.past - 選擇過去時段時的提示訊息
 * @param {Function} options.setSelection - 設定選擇狀態的函式
 * @param {Function} [options.onPanelReset] - 面板重置時的回調函式
 * @param {Function} [options.onPanelOpen] - 面板開啟時的回調函式
 * @returns {Function} 時間格點擊處理器函式
 */
  const makeOnClickTimeCell = ({
    periodBlockFn,
    searchDateRef,
    dialog,
    select,
    createReservationBlockRef,
    loadingControl,
    messages,
    setSelection,
    onPanelReset,
    onPanelOpen,
  }) => {
  /**
   * 時間格點擊處理器
   * @param {Object} scope - Element UI 表格的 slot-scope 物件
   */
    return (scope) => {
    // 檢查時段是否可預約
      const status = periodBlockFn(scope).status
      if (status !== 'available') {
        window.$message.warning(messages.notAvailable)
        return
      }

      // 檢查是否選擇過去的時間
      const dateStr = dayjs(searchDateRef.value).format('YYYY-MM-DD')
      const selectedDateTime = dayjs(`${dateStr} ${scope.column.label}`)
      if (selectedDateTime.isBefore(dayjs())) {
        window.$message.warning(messages.past)
        return
      }

      // 設定選擇狀態
      setSelection({ scope, select, searchDate: searchDateRef })

      // 處理面板狀態
      if (dialog.create) {
      // 面板已開啟，重置內容
        loadingControl.createReservationArea = true
        if (onPanelReset) {
          onPanelReset({ createReservationBlockRef, loadingControl })
        }
        setTimeout(() => {
          loadingControl.createReservationArea = false
        }, 300)
      } else {
      // 開啟面板
        dialog.create = true
        if (onPanelOpen) {
          onPanelOpen()
        }
      }
    }
  }

  return {
    times,
    show,
    filteredTimes,
    genTimes,
    attachAvailableTimes,
    periodBlock,
    trans,
    makeOnClickTimeCell,
  }
}
